import { useState, useCallback } from 'react';
import axios from 'axios';
import { useUser } from '@store';
import { Lanes, Locations } from '@gql/base-types';
import { useGetLaneByCustomerAndLocationsLazyQuery } from '../queries/getLaneByCustomerAndLocations.query.generated';
import { useLaneEnrichment } from '@features/moveCreation/hooks/useLaneEnrichment';
import { mergeLaneEstimates } from '@features/moveCreation/utils/laneEnrichment';

interface UseTimelineLaneBuilderResult {
  buildLane: (rooftopLocation: Locations, destinationLocation: Locations, rooftopId: number) => Promise<Lanes | null>;
  loading: boolean;
  error: string | null;
  lane: Lanes | null;
  inverseLane: Lanes | null;
  /** True while a freshly-built shell lane is still being enriched with routing estimates. */
  enriching: boolean;
  clearLane: () => void;
}

/**
 * Hook for building lanes in appointment mode for timeline planning
 * Checks for existing lanes before attempting to build new ones
 */
export const useTimelineLaneBuilder = (): UseTimelineLaneBuilderResult => {
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lane, setLane] = useState<Lanes | null>(null);
  const [inverseLane, setInverseLane] = useState<Lanes | null>(null);
  // Bumped by every build and clear, so enrichment updates for an abandoned pair are dropped.
  const [laneRequest, setLaneRequest] = useState(0);
  const [getLaneQuery] = useGetLaneByCustomerAndLocationsLazyQuery();

  const buildLane = useCallback(
    async (rooftopLocation: Locations, destinationLocation: Locations, rooftopId: number): Promise<Lanes | null> => {
      if (!rooftopLocation?.id || !destinationLocation?.id || rooftopLocation.id === destinationLocation.id) {
        setError('Invalid locations provided');
        return null;
      }

      setLaneRequest(request => request + 1);
      setLoading(true);
      setError(null);

      try {
        // Step 1: Check if lane already exists
        const existingLaneResult = await getLaneQuery({
          variables: {
            customerId: rooftopId,
            pickupId: rooftopLocation.id,
            deliveryId: destinationLocation.id,
          },
        });

        // If lane exists, check if it has inverse relationship
        if (existingLaneResult.data?.lanes && existingLaneResult.data.lanes.length > 0) {
          let existingLane = existingLaneResult.data.lanes[0] as Lanes;

          // Check if inverse exists
          if (existingLane.inverse) {
          } else {

            // Query for the inverse lane (destination -> rooftop)
            const inverseLaneResult = await getLaneQuery({
              variables: {
                customerId: rooftopId,
                pickupId: destinationLocation.id,  // Swapped: destination is pickup for return
                deliveryId: rooftopLocation.id,     // Swapped: rooftop is delivery for return
              },
            });

            if (inverseLaneResult.data?.lanes && inverseLaneResult.data.lanes.length > 0) {
              const inverseL = inverseLaneResult.data.lanes[0] as Lanes;

              // Manually attach inverse to existing lane
              existingLane = {
                ...existingLane,
                inverse: inverseL,
              };
              setInverseLane(inverseL);
            } else {
            }
          }

          setLane(existingLane);
          setLoading(false);
          return existingLane;
        }

        // Step 2: Lane doesn't exist, build a new one

        // Get the user token from the store
        const userToken = await user?.getToken();

        // Build the lane input (same format as MovePlannerLaneForm)
        const laneInput = {
          customerId: rooftopId,
          pickupId: rooftopLocation.id,
          deliveryId: destinationLocation.id,
          pickup: rooftopLocation,
          delivery: destinationLocation,
        };

        // Call the netlify function to build the full lane with Google Maps duration
        const buildRes = await axios({
          method: 'POST',
          url: '/.netlify/functions/buildLaneAndInverse',
          data: {
            lane: laneInput,
          },
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        });

        if (buildRes?.data?.upsertableLanes && buildRes.data.upsertableLanes.length > 0) {
          const [builtLane, builtInverseLane] = buildRes.data.upsertableLanes;

          // The endpoint inserts both lane rows; attach the inverse here since the
          // response carries the pair as two rows, not the `inverse` relationship.
          const laneWithInverse = {
            ...builtLane,
            inverse: builtInverseLane,
          };


          setLane(laneWithInverse);
          setInverseLane(builtInverseLane);
          setLoading(false);
          return laneWithInverse;
        }

        setError('Failed to build lane - no data returned');
        setLoading(false);
        return null;
      } catch (err) {
        console.error('Failed to build planning lane:', err);

        // Check if it's the "Two lanes already exist" error
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (errorMessage.includes('Two lanes already exist')) {
          setError('Lane already exists for these locations. Please try refreshing the page.');
        } else {
          setError('Failed to build lane. Please try again.');
        }

        setLoading(false);
        return null;
      }
    },
    [user, getLaneQuery]
  );

  const clearLane = useCallback(() => {
    setLaneRequest(request => request + 1);
    setLane(null);
    setInverseLane(null);
    setError(null);
  }, []);

  // Newly-built lanes are unenriched shells (no distance/duration until the backend
  // computes routing asynchronously). Follow the built lane until enrichment completes,
  // then merge the estimates in so time-slot generation has a real duration to work with.
  const handleLaneEnriched = useCallback((enriched: Lanes) => {
    setLane(prev => (prev && prev.id === enriched.id ? mergeLaneEstimates(prev, enriched) : prev));
  }, []);
  const { enriching } = useLaneEnrichment(lane, `timeline:${laneRequest}`, handleLaneEnriched);

  return {
    buildLane,
    loading,
    error,
    lane,
    inverseLane,
    enriching,
    clearLane,
  };
};
